<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import AgentLayout from 'dashboard/components-next/aiAgents/agent/AgentLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useBrain } from './useBrain';

// Brain editor (D09): edits the single implicit draft under optimistic
// locking. A 409 means someone else edited — offer reload, never overwrite.
const BLOCK_KEYS = [
  'personality',
  'environment',
  'tone',
  'goal',
  'guardrails',
  'tools',
];

const { t } = useI18n();
const store = useStore();
const { brain, fetchBrain } = useBrain();

const uiFlags = useMapGetter('aiAgentBrains/getUIFlags');
const isSaving = computed(() => uiFlags.value.updatingItem);

const blocks = reactive(
  Object.fromEntries(BLOCK_KEYS.map(key => [key, '']))
);
const systemPrompt = ref('');
const hasConflict = ref(false);
const isHydrated = ref(false);

const hydrateFromDraft = () => {
  const draft = brain.value.draft_config || {};
  const promptBlocks = draft.prompt_blocks || {};
  BLOCK_KEYS.forEach(key => {
    blocks[key] = promptBlocks[key] || '';
  });
  systemPrompt.value = draft.system_prompt || '';
  isHydrated.value = true;
};

watch(
  () => brain.value.id,
  id => {
    if (id && !isHydrated.value) hydrateFromDraft();
  },
  { immediate: true }
);

const save = async () => {
  try {
    await store.dispatch('aiAgentBrains/update', {
      id: brain.value.id,
      draftConfig: {
        ...(brain.value.draft_config || {}),
        prompt_blocks: { ...blocks },
        system_prompt: systemPrompt.value,
      },
      lockVersion: brain.value.lock_version,
    });
    hasConflict.value = false;
    useAlert(t('AI_AGENTS.BRAIN_EDITOR.SAVED'));
  } catch (error) {
    if (error.response?.status === 409) {
      hasConflict.value = true;
    } else {
      useAlert(t('AI_AGENTS.AGENT.PUBLISH.ERROR'));
    }
  }
};

const reloadDraft = async () => {
  await fetchBrain();
  hydrateFromDraft();
  hasConflict.value = false;
};
</script>

<template>
  <AgentLayout v-if="brain.id" :brain="brain">
    <div class="flex flex-col gap-4 max-w-3xl">
      <div>
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('AI_AGENTS.BRAIN_EDITOR.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.BRAIN_EDITOR.DESCRIPTION') }}
        </p>
      </div>

      <div
        v-if="hasConflict"
        class="flex items-center justify-between gap-3 px-3 py-2 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
      >
        {{ t('AI_AGENTS.AGENT.CONFLICT.MESSAGE') }}
        <Button
          :label="t('AI_AGENTS.AGENT.CONFLICT.RELOAD')"
          color="amber"
          size="sm"
          @click="reloadDraft"
        />
      </div>

      <label
        v-for="key in BLOCK_KEYS"
        :key="key"
        class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
      >
        {{ t(`AI_AGENTS.BRAIN_EDITOR.BLOCKS.${key.toUpperCase()}`) }}
        <textarea
          v-model="blocks[key]"
          rows="5"
          class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
        />
      </label>

      <label
        class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
      >
        {{ t('AI_AGENTS.BRAIN_EDITOR.SYSTEM_PROMPT') }}
        <textarea
          v-model="systemPrompt"
          rows="8"
          class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
        />
      </label>

      <div class="flex justify-end">
        <Button
          :label="t('AI_AGENTS.BRAIN_EDITOR.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="save"
        />
      </div>
    </div>
  </AgentLayout>
  <div
    v-else
    class="flex items-center justify-center w-full h-full text-n-slate-11"
  >
    <Spinner />
  </div>
</template>
